import {
  ActionError,
  Button,
  Checkbox,
  ChoiceRow,
  Field,
  FieldGrid,
  FormBlock,
  FormFooter,
  Input,
  ListRow,
  ListRows,
  PageHeader,
  PageTitle,
  ReadOnlyField,
  ReadingColumn,
  SearchInput,
  Select,
  Skeleton,
  formatCurrency,
  formatPhone,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { usePatientSearch } from '@/features/anamneses/api'
import { usePatient } from '@/features/patients/api'
import { usePractitioners } from '@/features/practitioners/api'
import { useProcedures } from '@/features/procedures/api'
import { isApiError } from '@/lib/api'
import { brDateToIso, isoToBrDate, maskDate } from '@/lib/dates'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import { useCreateTreatmentPlan, type CreateTreatmentPlanInput } from './api'
import { MSG_CREATED, isValidTime } from './labels'

/**
 * "+ Novo plano" (docs/areas/planos-de-tratamento.md, RN-PLA-03; P-PLA-05). Arquétipo: cadastro.
 * Escolhe a paciente, os procedimentos do catálogo, a profissional, a data e o horário (opcionais)
 * e, sem nascimento no cadastro, a idade na data do procedimento. A API cria o atendimento sem
 * documentos e o plano; se o atendimento já tem um plano, oferece abri-lo (RN-PLA-02).
 */

interface FormState {
  procedureIds: string[]
  practitionerId: string
  procedureDate: string
  procedureTime: string
  ageAtProcedure: string
  isModelPatient: boolean
}

type FormErrors = Partial<Record<'patientId' | keyof FormState, string>>

const MSG_PATIENT = 'Escolha a paciente.'
const MSG_PROCEDURES = 'Escolha pelo menos um procedimento.'
const MSG_PROCEDURES_MAX = 'Um plano aceita até 10 procedimentos.'
const MSG_PRACTITIONER = 'Escolha a profissional responsável.'
const MSG_AGE = 'Informe a idade da paciente na data do procedimento.'
const MSG_DATE = 'Use o formato dd/mm/aaaa.'
const MSG_TIME = 'Use o formato HH:mm, como 14:30.'
const MSG_TIME_WITHOUT_DATE = 'Informe a data do procedimento junto com o horário.'

function validate(form: FormState, patientId: string | null, needsAge: boolean): FormErrors {
  const errors: FormErrors = {}
  if (!patientId) errors.patientId = MSG_PATIENT
  if (form.procedureIds.length === 0) errors.procedureIds = MSG_PROCEDURES
  else if (form.procedureIds.length > 10) errors.procedureIds = MSG_PROCEDURES_MAX
  if (!form.practitionerId) errors.practitionerId = MSG_PRACTITIONER
  const date = form.procedureDate.trim()
  if (date && brDateToIso(date) === null) errors.procedureDate = MSG_DATE
  const time = form.procedureTime.trim()
  if (time && !isValidTime(time)) errors.procedureTime = MSG_TIME
  else if (time && !date) errors.procedureTime = MSG_TIME_WITHOUT_DATE
  if (needsAge) {
    const age = Number(form.ageAtProcedure)
    if (form.ageAtProcedure.trim() === '' || !Number.isInteger(age) || age < 0 || age > 120) {
      errors.ageAtProcedure = MSG_AGE
    }
  }
  return errors
}

export function NewTreatmentPlanPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const id = useId()
  const [patientId, setPatientId] = useState<string | null>(params.get('patientId'))
  const [form, setForm] = useState<FormState>({
    procedureIds: [],
    practitionerId: '',
    procedureDate: '',
    procedureTime: '',
    ageAtProcedure: '',
    isModelPatient: false,
  })
  const [touched, setTouched] = useState<Set<string>>(() => new Set())
  const [submitted, setSubmitted] = useState(false)
  const patient = usePatient(patientId ?? undefined)
  const procedures = useProcedures({ filter: 'all', perPage: 50 })
  const practitioners = usePractitioners({ filter: 'active', perPage: 50 })
  const create = useCreateTreatmentPlan()

  const needsAge = Boolean(patientId) && patient.isSuccess && patient.data.birthDate === null
  const found = validate(form, patientId, needsAge)
  const refused = create.isError ? fieldErrors(create.error) : {}
  const visible = (key: keyof FormErrors) =>
    (submitted || touched.has(key) ? found[key] : undefined) ?? refused[key] ?? null
  const touch = (key: keyof FormErrors) =>
    setTouched((current) => (current.has(key) ? current : new Set(current).add(key)))
  const update = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }))

  const existingPlanId =
    create.isError && isApiError(create.error) && create.error.code === 'conflict'
      ? (create.error.details.treatmentPlanId as string | undefined)
      : undefined

  function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    if (Object.keys(found).length > 0 || !patientId) return
    const date = form.procedureDate.trim() ? brDateToIso(form.procedureDate) : null
    const input: CreateTreatmentPlanInput = {
      patientId,
      procedureIds: form.procedureIds,
      practitionerId: form.practitionerId,
      ...(date ? { procedureDate: date } : {}),
      ...(date && form.procedureTime.trim() ? { procedureTime: form.procedureTime.trim() } : {}),
      ...(form.isModelPatient ? { isModelPatient: true } : {}),
      ...(needsAge ? { ageAtProcedure: Number(form.ageAtProcedure) } : {}),
    }
    create.mutate(input, {
      onSuccess: (plan) => {
        toast(MSG_CREATED)
        navigate(routes.treatmentPlan(plan.id))
      },
    })
  }

  const back = patientId ? routes.patientTab(patientId, 'treatment-plans') : routes.treatmentPlans
  const selectedTotal = (procedures.data?.data ?? [])
    .filter((p) => form.procedureIds.includes(p.id))
    .reduce((sum, p) => sum + p.amountCents, 0)

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Planos de Tratamento', href: routes.treatmentPlans },
          { label: 'Novo plano' },
        ]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={back}>Voltar</Link>
          </Button>
        }
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Novo plano"
            subtitle="O plano nasce de um atendimento: paciente, procedimentos e profissional vêm do cadastro e não mudam depois."
          />
          <form noValidate onSubmit={submit} className="mt-6 flex flex-col gap-4">
            <FormBlock
              title="Paciente"
              note="Aparece no cabeçalho do plano e no PDF. O nome e a idade são copiados agora."
            >
              {patientId ? (
                <PatientChosen
                  patientId={patientId}
                  onChange={params.get('patientId') ? undefined : () => setPatientId(null)}
                />
              ) : (
                <PatientSearch onSelect={(chosen) => setPatientId(chosen)} />
              )}
              {visible('patientId') ? (
                <p role="alert" className="font-body text-plum mt-2 text-[12px] leading-[1.5]">
                  {visible('patientId')}
                </p>
              ) : null}
              {needsAge ? (
                <FieldGrid className="mt-4">
                  <Field
                    id={`${id}-age`}
                    label="Idade na data do procedimento"
                    hint="A paciente não tem nascimento no cadastro; o plano não nasce sem a idade."
                    error={visible('ageAtProcedure')}
                  >
                    <Input
                      inputMode="numeric"
                      value={form.ageAtProcedure}
                      onChange={(e) =>
                        update({ ageAtProcedure: e.target.value.replace(/\D/g, '').slice(0, 3) })
                      }
                      onBlur={() => touch('ageAtProcedure')}
                    />
                  </Field>
                </FieldGrid>
              ) : null}
            </FormBlock>

            <FormBlock
              title="Procedimentos"
              note="Do catálogo da clínica; o valor de cada um é copiado para o plano. De 1 a 10."
            >
              {procedures.isError ? (
                <LoadError
                  title="Não conseguimos carregar os procedimentos"
                  error={procedures.error}
                  onRetry={() => void procedures.refetch()}
                />
              ) : procedures.isPending ? (
                <div
                  role="status"
                  aria-label="Carregando os procedimentos"
                  className="flex flex-col gap-2"
                >
                  <Skeleton className="h-[48px] w-full" />
                  <Skeleton className="h-[48px] w-full" />
                </div>
              ) : (procedures.data?.data ?? []).length === 0 ? (
                <p className="font-body text-secondary text-[12px] leading-[1.6]">
                  Nenhum procedimento no catálogo da clínica.{' '}
                  <Link to={routes.admin.procedures}>Cadastrar procedimentos</Link>
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {(procedures.data?.data ?? []).map((procedure) => (
                    <ChoiceRow
                      key={procedure.id}
                      htmlFor={`${id}-proc-${procedure.id}`}
                      control={
                        <Checkbox
                          id={`${id}-proc-${procedure.id}`}
                          aria-label={procedure.name}
                          checked={form.procedureIds.includes(procedure.id)}
                          onCheckedChange={(checked) =>
                            update({
                              procedureIds:
                                checked === true
                                  ? [...form.procedureIds, procedure.id]
                                  : form.procedureIds.filter((x) => x !== procedure.id),
                            })
                          }
                        />
                      }
                      trailing={
                        <span className="font-body text-secondary text-[13px] font-light tabular-nums">
                          {formatCurrency(procedure.amountCents / 100, { compact: true })}
                        </span>
                      }
                    >
                      {procedure.name}
                    </ChoiceRow>
                  ))}
                </div>
              )}
              {form.procedureIds.length > 0 ? (
                <p className="font-body text-secondary mt-3 text-[12px] leading-[1.6]">
                  {form.procedureIds.length === 1
                    ? '1 procedimento'
                    : `${form.procedureIds.length} procedimentos`}{' '}
                  · total {formatCurrency(selectedTotal / 100, { compact: true })}
                </p>
              ) : null}
              {visible('procedureIds') ? (
                <p role="alert" className="font-body text-plum mt-2 text-[12px] leading-[1.5]">
                  {visible('procedureIds')}
                </p>
              ) : null}
            </FormBlock>

            <FormBlock
              title="Profissional e data"
              note="A profissional responde tecnicamente pelo plano. Data e horário alimentam a agenda e podem ser informados depois."
            >
              <FieldGrid>
                <Field
                  id={`${id}-practitioner`}
                  label="Profissional responsável"
                  error={visible('practitionerId')}
                >
                  <Select
                    value={form.practitionerId}
                    placeholder="Selecione"
                    disabled={practitioners.isPending}
                    onChange={(e) => update({ practitionerId: e.target.value })}
                    onBlur={() => touch('practitionerId')}
                  >
                    {(practitioners.data?.data ?? []).map((practitioner) => (
                      <option key={practitioner.id} value={practitioner.id}>
                        {titleCase(practitioner.name)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <ChoiceRow
                  className="self-end"
                  htmlFor={`${id}-model`}
                  control={
                    <Checkbox
                      id={`${id}-model`}
                      checked={form.isModelPatient}
                      onCheckedChange={(checked) => update({ isModelPatient: checked === true })}
                    />
                  }
                  description="Atendimento com contrato de paciente modelo, quando os documentos forem gerados."
                >
                  Paciente modelo
                </ChoiceRow>
                <Field
                  id={`${id}-date`}
                  label="Data do procedimento"
                  optional
                  error={visible('procedureDate')}
                >
                  <Input
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    value={form.procedureDate}
                    onChange={(e) => update({ procedureDate: maskDate(e.target.value) })}
                    onBlur={() => touch('procedureDate')}
                  />
                </Field>
                <Field id={`${id}-time`} label="Horário" optional error={visible('procedureTime')}>
                  <Input
                    inputMode="numeric"
                    placeholder="HH:mm"
                    value={form.procedureTime}
                    onChange={(e) =>
                      update({ procedureTime: e.target.value.replace(/[^\d:]/g, '').slice(0, 5) })
                    }
                    onBlur={() => touch('procedureTime')}
                  />
                </Field>
              </FieldGrid>
            </FormBlock>

            {create.isError ? (
              <ActionError
                title={
                  existingPlanId
                    ? 'Este atendimento já tem um plano'
                    : 'Não foi possível criar o plano'
                }
                description={
                  existingPlanId
                    ? 'Abra o plano existente em vez de criar outro. O que você escolheu continua nesta tela.'
                    : `${errorMessage(create.error)} O que você escolheu continua nesta tela.`
                }
                actions={
                  existingPlanId ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={routes.treatmentPlan(existingPlanId)}>Abrir plano</Link>
                    </Button>
                  ) : isApiError(create.error) && create.error.code === 'conflict' && patientId ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={routes.patientTab(patientId, 'details')}>Completar cadastro</Link>
                    </Button>
                  ) : undefined
                }
              />
            ) : null}

            <FormFooter
              secondary={
                <Button variant="outline" asChild>
                  <Link to={back}>Cancelar</Link>
                </Button>
              }
              primary={
                <Button type="submit" loading={create.isPending} loadingLabel="Criando…">
                  Criar plano
                </Button>
              }
            />
          </form>
        </ReadingColumn>
      </Page>
    </>
  )
}

/** A paciente já escolhida: o nome e o que o plano copia dela. */
function PatientChosen({ patientId, onChange }: { patientId: string; onChange?: () => void }) {
  const patient = usePatient(patientId)
  if (patient.isError) {
    return (
      <LoadError
        title="Não conseguimos carregar o cadastro da paciente"
        error={patient.error}
        onRetry={() => void patient.refetch()}
      />
    )
  }
  if (patient.isPending) {
    return (
      <div role="status" aria-label="Carregando a paciente">
        <Skeleton className="h-[44px] w-full" />
      </div>
    )
  }
  const data = patient.data
  const line = [
    data.birthDate
      ? `${isoToBrDate(data.birthDate)}${data.age !== null ? ` · ${data.age} anos` : ''}`
      : 'sem data de nascimento no cadastro',
    data.whatsapp ? formatPhone(data.whatsapp) : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="flex flex-col gap-3">
      <ReadOnlyField label="Paciente" value={titleCase(data.name)} note={null} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-body text-secondary text-[12px] leading-[1.6]">{line}</span>
        {onChange ? (
          <Button variant="text" size="sm" onClick={onChange}>
            Trocar paciente
          </Button>
        ) : (
          <Button variant="text" size="sm" asChild>
            <Link to={routes.patient(data.id)}>Abrir ficha</Link>
          </Button>
        )}
      </div>
    </div>
  )
}

/** A busca da paciente na lista global (`GET /patients?search=`), como no envio de anamnese. */
function PatientSearch({ onSelect }: { onSelect: (patientId: string) => void }) {
  const [search, setSearch] = useState('')
  const term = useDebounced(search.trim())
  const query = usePatientSearch(term)
  const patients = query.data?.data ?? []
  const lineClass = 'font-body text-secondary text-[13px] leading-[1.6] font-light'
  return (
    <div className="flex flex-col gap-3">
      <SearchInput
        autoFocus
        value={search}
        onChange={setSearch}
        placeholder="Buscar paciente por nome, CPF ou telefone"
        aria-label="Buscar paciente"
      />
      {term.length < 2 ? (
        <p className={lineClass}>
          Digite pelo menos 2 letras do nome. O plano parte sempre de uma paciente cadastrada.
        </p>
      ) : query.isError ? (
        <LoadError
          title="Não conseguimos buscar as pacientes"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <div role="status" aria-label="Buscando pacientes" className="flex flex-col gap-2">
          <Skeleton className="h-[48px] w-full" />
          <Skeleton className="h-[48px] w-full" />
        </div>
      ) : patients.length === 0 ? (
        <div>
          <p className={lineClass}>
            Nenhuma paciente para "{term}". A busca cobre nome, CPF, e-mail e telefone.
          </p>
          <Button variant="outline" className="mt-3" asChild>
            <Link to={routes.newPatient}>Criar paciente</Link>
          </Button>
        </div>
      ) : (
        <ListRows role="group" aria-label="Pacientes encontradas">
          {patients.map((patient) => (
            <ListRow
              key={patient.id}
              title={titleCase(patient.name)}
              meta={
                [patient.whatsapp ? formatPhone(patient.whatsapp) : null, patient.email]
                  .filter(Boolean)
                  .join(' · ') || 'Sem WhatsApp nem e-mail no cadastro'
              }
              aria-label={`Escolher ${titleCase(patient.name)}`}
              onClick={() => onSelect(patient.id)}
            />
          ))}
        </ListRows>
      )}
    </div>
  )
}
